import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styles from './index.modules.scss';
import { Carousel } from '@mantine/carousel';
import { updateUnitData, getUnitData } from '@starflow/app/helpers/flow';
import { setSharedData } from '@starflow/app/store';
import {
  Image,
  Button,
  PromptInput,
  Text,
  Descriptions,
  DescriptionsItem,
  notifySuccess,
  notifyWarn,
} from '@shakkerai/pro-components';
import { Icon } from '@shakkerai/components';
import { useGenHistoryItemEventHooks } from '../../hooks/eventHooks.js';
import {
  useViewDetailState,
  useLargeImageViewState,
  useGenHistoryVisible,
  useComfirmModalState,
} from '../../store/base';
import { eventBus } from '@starflow/app/helpers';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { translate } from '@shakkerai/util-i18n';
import { defaultStyle } from '@shakkerai/pro-constants';
import { applyModel, applyPresetModel, getActiveGeneratorId, isMainland } from '@shakkerai/pro-utils';
import { ImageShareButton } from '@shakkerai/pro-components';
import { presetCheckpointsMap } from '@shakkerai/pro-models';

const modelImage =
  'https://models-online-persist.shakker.ai/img/fb61b1d78e1a4f69ac22cc70860b6243/faf4858e32323ebbd52648f3dca3ec2bea63ff4ec679918075d0d33527b65be7.png';

const ImageWrapper = (props) => {
  const { imageInfo, onViewLargeImageClick } = props;

  const [collected, setCollected] = useState(imageInfo.groupId > 0);
  const { onCollect, onDelete, onDownload, onCancelCollect } = useGenHistoryItemEventHooks({
    onCollectDone() {
      setCollected(!collected);
      eventBus.emit('event_imageDetailRefresh');
    },
    onDeleteDone() {
      eventBus.emit('event_imageDetailRefresh');
    },
    itemInfo: imageInfo,
  });

  const { showComfirmModal } = useComfirmModalState();

  const onDeleteInner = () => {
    showComfirmModal({
      content: translate('history_deletesingleimage'),
      subContent: translate('history_deletecheck_sub'),
      onConfirmCallback: onDelete,
    });
  };

  const url = convert2CompressedUrl(imageInfo.imageId, 500);

  const handleDragStart = () => {
    const { generateId, outputId, width, height, imageId: url } = imageInfo;
    setSharedData('dragging-element', {
      source: 'history',
      innerTaskId: generateId,
      generateId,
      outputId,
      width,
      height,
      url,
    });
  };

  const renderOperationBar = () => {
    return (
      <div className={styles.operationBar}>
        <ImageShareButton imageInfo={imageInfo}>
          <div className="leading-none">
            <Icon className={`${styles.oIcon} icon-proshare`} fontSize={16} color="white" />
          </div>
        </ImageShareButton>
        <Icon className={`${styles.oIcon} icon-prodownloa`} fontSize={16} color="white" onClick={onDownload} />
        <Icon
          className={`${styles.oIcon} ${collected ? 'icon-procollect' : 'icon-procollect'} cursor-pointer`}
          fontSize={16}
          color={collected ? '#FFF970' : '#FFFFFF'}
          onClick={collected ? onCancelCollect : onCollect}
        />
        <Icon className={`${styles.oIcon} icon-prodelet`} fontSize={16} color="white" onClick={onDeleteInner} />
      </div>
    );
  };

  const [imageLoaded, setLoaded] = useState(false);

  return (
    <div className={styles.imageC}>
      <div className={styles.imageWrapper}>
        <div className="relative max-h-full">
          <Image
            src={url}
            onClick={onViewLargeImageClick}
            draggable
            onDragStart={handleDragStart}
            className={styles.image}
            onLoad={() => {
              setLoaded(true);
            }}
          ></Image>
          {imageLoaded && renderOperationBar()}
        </div>
      </div>
    </div>
  );
};

/**
 * 只负责渲染详情内部信息，标题、操作按钮、分享落地、数据请求由外部控制
 */
export const ImageDetail = (props) => {
  const { taskDetail, source, detailModel = true, updateImageIndex, imageInfo } = props;
  const { param, images, referenceImage, taskParam } = taskDetail;

  const { checkpoint, presetBaseModelId, loraModels: originLoraModels } = taskParam.projectData;
  const isStarFlow = presetBaseModelId === 'base_checkpoint';

  // 隐藏暗策略的模型
  const loraModels = globalThis.STARFLOW_DEBUG_ENABLED
    ? originLoraModels || []
    : (originLoraModels || []).filter((item) => !item.isHiddenStrategy);

  const style = useMemo(() => {
    return taskDetail?.taskParam?.projectData?.style || defaultStyle;
  }, [taskDetail]);

  const { currentViewedImageIndex } = useViewDetailState();

  const [expanded, setExpanded] = useState(!detailModel);
  // 大图查看
  const { setLargeViewerVisible } = useLargeImageViewState();
  const [currentInnerViewIndex, setCurrentInnerViewIndex] = useState(currentViewedImageIndex);

  const imgs = useMemo(() => {
    return images?.map((item) => {
      return {
        src: item.imageId,
        ...item,
      };
    });
  }, [images]);

  const currentViewedImageInfo = useMemo(() => {
    if (images && images.length > 0) {
      return images[currentInnerViewIndex];
    } else {
      return null;
    }
  }, [currentInnerViewIndex, images]);

  useEffect(() => {
    updateImageIndex && updateImageIndex(currentInnerViewIndex);
    // eslint-disable-next-line
  }, [currentInnerViewIndex]);

  const { onImport, onAddAsReference, onAddToCanvas, onAddReferenceAsReference } = useGenHistoryItemEventHooks({
    itemInfo: currentViewedImageInfo,
    source: source,
  });

  const onViewLargeImageClick = useCallback(
    (index) => {
      setLargeViewerVisible(true, imgs, index);
    },
    [imgs, setLargeViewerVisible],
  );

  const { hideHistoryModelsBar } = useGenHistoryVisible();

  // 是否只有Checkpoint
  const isOnlyCheckpoint = !loraModels || loraModels?.length === 0;

  const includeAnyLora = !isOnlyCheckpoint;

  const handleApplyPresetModel = () => {
    applyPresetModel(presetBaseModelId);
  };

  /**
   * 应用模型
   */
  const applyCheckpoint = (model) => {
    const id = getActiveGeneratorId();
    let ls = getUnitData(id, 'loraModels') || [];

    if (model?.modelType?.toLowerCase() === 'checkpoint') {
      updateUnitData(id, { checkpoint: model });
    } else {
      if (!ls.find((i) => i?.versionId === model?.versionId)) {
        ls = [...ls, model];
      }
      if (ls?.length > 5) {
        return notifyWarn(translate(translate('model_lora_limitetoast')));
      } else {
        updateUnitData(id, { loraModels: [...ls] });
      }
    }
  };

  /**
   * 应用全部 全部覆盖
   */
  const applyAll = (showNotify = true) => {
    const update = () => {
      const id = getActiveGeneratorId();

      if (presetBaseModelId) {
        applyModel(id, presetBaseModelId);
        updateUnitData(id, {
          checkpoint: null,
          presetBaseModelId,
        });
      } else {
        updateUnitData(id, {
          checkpoint: { ...checkpoint },
          loraModels: loraModels.map((lora) => ({ ...lora })),
        });
      }

      showNotify && notifySuccess(`All applied`);
    };

    update();
  };

  const renderModelInfos = () => {
    if (presetBaseModelId) {
      const presetBaseModelDetail = presetCheckpointsMap[presetBaseModelId] || presetCheckpointsMap['base_checkpoint'];

      return (
        <div className={styles.group}>
          <div className={`${styles.title} !h-[20px]`}>Model</div>
          <div className={`${styles.rImageC} ${includeAnyLora ? '!h-[92px] !w-[236px]' : ''}`}>
            <Image
              className={`${styles.rImage} ${includeAnyLora ? '!h-full !w-full' : ''}`}
              fit="cover"
              src={convert2CompressedUrl(
                presetBaseModelDetail?.imageIcon2 || presetBaseModelDetail?.imageIcon || modelImage,
                200,
              )}
            />
            <div className={styles.rText}>{presetBaseModelDetail?.displayName || translate('model_default')}</div>

            {detailModel && includeAnyLora && (
              <div className={`${styles.applyMask}`}>
                <div className={`${styles.applyButton}`} onClick={() => handleApplyPresetModel()}>
                  Apply
                </div>
              </div>
            )}
          </div>

          {includeAnyLora && (
            <div className={styles.loraC}>
              {loraModels.map((i) => (
                <div className={styles.imageC} key={i.uuid}>
                  <Image src={convert2CompressedUrl(i?.imageUrl, 50)} />
                  {detailModel && (
                    <div className={styles.applyMask} onClick={() => applyCheckpoint(i)}>
                      Apply
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {detailModel && (
            <div
              className={styles.rButton}
              onClick={() => {
                applyAll();
              }}
            >
              {isOnlyCheckpoint ? translate('style_use') : 'Apply All'}
            </div>
          )}
        </div>
      );
    }
    return (
      <div className={styles.group}>
        <div className={`${styles.title} !h-[20px]`}>Model</div>
        <div className={`${styles.rImageC} ${includeAnyLora ? '!h-[92px] !w-[236px]' : ''}`}>
          <Image
            className={`${styles.rImage} ${includeAnyLora ? '!h-full !w-full' : ''}`}
            fit="cover"
            src={convert2CompressedUrl(checkpoint?.imageUrl || modelImage, 200)}
          ></Image>
          <div className={styles.rText}>{checkpoint?.name || translate('model_default')}</div>

          <div className={styles.label}>{checkpoint?.baseType || 'Default Type'}</div>

          {detailModel && includeAnyLora && (
            <div className={`${styles.applyMask}`}>
              <div className={`${styles.applyButton}`} onClick={() => applyCheckpoint(checkpoint)}>
                Apply
              </div>
            </div>
          )}
        </div>

        {includeAnyLora && (
          <div className={styles.loraC}>
            {loraModels.map((i) => (
              <div className={styles.imageC} key={i.uuid}>
                <Image src={convert2CompressedUrl(i?.imageUrl, 50)} />
                {detailModel && (
                  <div className={styles.applyMask} onClick={() => applyCheckpoint(i)}>
                    Apply
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {detailModel && (
          <div
            className={styles.rButton}
            onClick={() => {
              applyAll();
            }}
          >
            {isOnlyCheckpoint ? translate('style_use') : 'Apply All'}
          </div>
        )}
      </div>
    );
  };

  const renderReference = () => {
    if (style || referenceImage) {
      return (
        <div className={styles.referenceC}>
          {renderModelInfos()}
          {referenceImage ? (
            <div className={styles.group}>
              <div className={styles.title}>{translate('imgref_composition')}</div>
              <div className={styles.rImageC}>
                <Image className={styles.rImage} src={convert2CompressedUrl(referenceImage, 200)}></Image>
              </div>
              {detailModel && (
                <div
                  className={styles.rButton}
                  onClick={() => {
                    onAddReferenceAsReference();
                    hideHistoryModelsBar();
                  }}
                >
                  {translate('history_allimageref')}
                </div>
              )}
            </div>
          ) : null}
        </div>
      );
    } else {
      return null;
    }
  };

  return (
    <div className={styles.imageDetail}>
      <div className={`${styles.innerScroll} ${detailModel ? 'p-4' : ''} tiny-scrollbar`}>
        {detailModel && (
          <Carousel
            onSlideChange={(index) => setCurrentInnerViewIndex(index)}
            height={350}
            initialSlide={currentViewedImageIndex}
            draggable={false}
            className={styles.carousel}
          >
            {images?.map((item, index) => {
              const imageInfo = {
                ...item,
                innerTaskId: taskDetail.generateId,
                innerImageId: item.outputId,
                url: item.imageId,
                genInfo: taskParam?.projectData || {},
              };
              return (
                <Carousel.Slide key={item.outputId}>
                  <ImageWrapper imageInfo={imageInfo} onViewLargeImageClick={() => onViewLargeImageClick(index)} />
                </Carousel.Slide>
              );
            })}
          </Carousel>
        )}

        {detailModel && (
          <div className={styles.buttonGroup}>
            <Button
              iconClass="icon-a-proImportImages"
              className={styles.button}
              color="var(--color-light-200)"
              fSize={12}
              iSize={16}
              onClick={() => {
                onAddAsReference();
              }}
            >
              {translate('history_ref')}
            </Button>
            <Button
              iconClass="icon-a-proAddtoCanvas"
              className={styles.button}
              color="var(--color-light-200)"
              fSize={12}
              iSize={16}
              onClick={onAddToCanvas}
            >
              {translate('history_movetocanva')}
            </Button>
          </div>
        )}

        {detailModel && renderReference()}

        <div className="mb-3">
          <PromptInput
            title={translate('prompt')}
            value={param?.prompt}
            toastSuccessContent={translate('copy_prompt_toast')}
          ></PromptInput>
        </div>

        {detailModel && (
          <div className={`${styles.advance} mt-2`} onClick={() => setExpanded(!expanded)}>
            <Text color="#333" fw={500}>
              {translate('allsettings')}
            </Text>

            <Icon
              className={`${styles.aicon} icon-border_up ${expanded ? 'rotate-0' : 'rotate-180'}`}
              color="#222"
            ></Icon>
          </div>
        )}

        <div className={`${styles.hiddenArea} ${expanded ? 'block' : 'hidden'}`}>
          <div className="mb-3">
            <PromptInput
              title={translate('prompt_negative')}
              toastSuccessContent={translate('copy_negprompt_toast')}
              fold={true}
              defaultExpand={false}
              value={param?.negativePrompt}
            ></PromptInput>
          </div>

          {!detailModel && renderReference()}

          <div className={styles.dataArea}>
            <Descriptions>
              <DescriptionsItem label={translate('img_scale')}>
                {currentViewedImageInfo?.width}x{currentViewedImageInfo?.height}px
              </DescriptionsItem>
              <DescriptionsItem label={translate('parameter_steps')}>{param?.samplingStep}</DescriptionsItem>
              <DescriptionsItem label={translate('parameter_sampler')}>{param?.samplingMethodName}</DescriptionsItem>
              {!isStarFlow && (
                <DescriptionsItem label={translate('parameter_seed')}>{currentViewedImageInfo?.seed}</DescriptionsItem>
              )}
              <DescriptionsItem label={translate('parameter_cfg')}>{param?.cfgScale}</DescriptionsItem>
              {imageInfo?.styleCode && (
                <DescriptionsItem label={translate('style_code')}>{imageInfo?.styleCode}</DescriptionsItem>
              )}
            </Descriptions>
          </div>
        </div>
      </div>

      <div
        className={`${styles.bottomButton} ${detailModel ? 'mr-4 mb-4 ml-4' : ''}`}
        onClick={() => {
          if (detailModel) {
            applyAll(false);
            onImport();
            hideHistoryModelsBar();
          } else {
            window.location.href = `/`;
          }
        }}
      >
        {detailModel ? translate('parameter_applyall') : isMainland ? '去Picpic生成同款' : 'try it on shakker'}
      </div>
    </div>
  );
};
